import { allocationCellKey } from '@ovComponents/4-module/widgets/portfolio/components/allocationCellKey';
import type { SubAccountCustomAllocationInput } from '@ovComponents/4-module/widgets/portfolio/components/buildCustomAllocationInput';
import { SolveStatuses } from '@ovComponents/4-module/widgets/portfolio/components/lockedMatrixRedistribute';
import { fractionToAmountCents } from '@utils/allocationUnits';
import type { CellEditPatch } from './resolveCellEdit';

export type SavedAllocationDraft = {
  matrix: SubAccountCustomAllocationInput[];
  lockedCells: string[];
  uploadedCells?: string[];
};

// A saved matrix was a solved one (Continue needs an OK solve), so it comes
// back as solved: nothing waits on a Recalculate. Locks keep their cents the
// way a typed lock stores them ($0 accounts have none). An uploaded cell comes
// back locked and still marked uploaded, so uploadedCells stays a subset of
// lockedCells.
export const restoredDraftPatch = (
  { matrix, lockedCells, uploadedCells = [] }: SavedAllocationDraft,
  mvCentsOf: (subAccountId: string) => number,
): CellEditPatch => {
  const uploadedKeys = new Set(uploadedCells);
  const lockedKeys = new Set([...lockedCells, ...uploadedKeys]);
  const restoredUploads = new Set<string>();
  const draft = new Map<string, number>();
  const locks = new Map<string, number>();
  const lockCents = new Map<string, number>();

  for (const { subAccountId, overrides } of matrix) {
    for (const { financialProductId, customExpectedPercentage } of overrides) {
      const key = allocationCellKey(subAccountId, financialProductId);
      draft.set(key, customExpectedPercentage);
      if (!lockedKeys.has(key)) continue;
      if (uploadedKeys.has(key)) restoredUploads.add(key);
      locks.set(key, customExpectedPercentage);
      const mvCents = mvCentsOf(subAccountId);
      if (mvCents <= 0) continue;
      lockCents.set(
        key,
        fractionToAmountCents(customExpectedPercentage, mvCents),
      );
    }
  }

  return {
    lockedCells: locks,
    lockedAmountCents: lockCents,
    uploadedCells: restoredUploads,
    draft,
    solveStatus: SolveStatuses.OK,
  };
};
